import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createGroup } from '../../services/firestoreService';
import { uploadFile } from '../../services/fileUploadService';
import { APN_GROUP_LOGOS, DEFAULT_GROUP_IMAGE, resolveGroupImage } from '../../utils/groupImage';

const GroupCreate = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Networking',
    isPrivate: false,
    image: DEFAULT_GROUP_IMAGE
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const categories = [
    'Networking',
    'Industry Specific',
    'Career Development',
    'Education',
    'Regional',
    'Diversity & Inclusion',
    'Technology',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    try {
      const groupData = {
        ...formData,
        createdBy: currentUser.uid,
        members: [currentUser.uid],
        memberCount: 1,
        posts: [],
        moderators: [currentUser.uid],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const groupId = await createGroup(groupData, currentUser.uid);
      navigate(`/groups/${groupId}`);
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Error creating group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const selectLogo = (path) => setFormData(prev => ({ ...prev, image: path }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !currentUser) return;
    setUploadError('');
    setUploading(true);
    try {
      const result = await uploadFile(file, 'PROFILE_IMAGES', currentUser.uid);
      setFormData(prev => ({ ...prev, image: result.url }));
    } catch (err) {
      console.error('Group photo upload failed:', err);
      setUploadError(err.message || 'Upload failed. Please use a JPG/PNG under 5MB.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Create New Group</h1>
            <p className="text-gray-600 mt-2">Start a new professional group and connect with like-minded members.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Group Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue focus:border-transparent"
                placeholder="Enter group name"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                required
                rows={4}
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue focus:border-transparent"
                placeholder="Describe what this group is about"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group Photo
              </label>
              <div className="flex items-center gap-4 mb-3">
                <div className="h-20 w-20 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img src={resolveGroupImage(formData.image)} alt="Group preview" className="h-full w-full object-contain" />
                </div>
                <p className="text-sm text-gray-500">
                  Pick an APN logo or upload your own photo. You can change this later.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {APN_GROUP_LOGOS.map(logo => (
                  <button
                    type="button"
                    key={logo.path}
                    onClick={() => selectLogo(logo.path)}
                    title={logo.label}
                    className={`h-14 w-14 rounded-md border-2 bg-white flex items-center justify-center overflow-hidden ${formData.image === logo.path ? 'border-apn-blue' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <img src={`${process.env.PUBLIC_URL}${logo.path}`} alt={logo.label} className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md cursor-pointer hover:bg-gray-200">
                {uploading ? 'Uploading…' : 'Upload a photo'}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} className="hidden" />
              </label>
              {uploadError && <p className="text-sm text-red-500 mt-2">{uploadError}</p>}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPrivate"
                name="isPrivate"
                checked={formData.isPrivate}
                onChange={handleChange}
                className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
              />
              <label htmlFor="isPrivate" className="ml-2 block text-sm text-gray-700">
                Make this group private (members must be approved)
              </label>
            </div>

            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={() => navigate('/groups')}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GroupCreate;
